import {Injectable} from '@angular/core';
import {from} from 'rxjs';
import {groupBy, mergeMap, toArray} from 'rxjs/operators';
import {cloneDeep, get} from 'lodash';

@Injectable()
export class GroupingAndOrderingService {

  private updatedData: boolean = false;

  constructor() { }


  /**
   * This method to flattern the group of elemenents to a single array
   * @param groups
   */
  flatternArray(groups: any[]) {
    const flatten = arr => arr.reduce(
      (a, b) => a.concat(Array.isArray(b) ? flatten(b) : b), []
    );
    const flatArray = flatten(groups);
    return flatArray;
  }

  /**
   * Function to sort an array based on orderParam attribute.
   * This function sort based on string.
   * The sorting is in descending order.
   * @param groups
   * @param orderParam
   * @param order
   */
  private sortByParam(groups: any[], orderParam: string, order: string) {
      groups.forEach( (value) => {
        value.sort((x: any, y: any) =>{
          if(this.isDate(x[orderParam], y[orderParam])) {
            let a = new Date(x[orderParam]);
            let b = new Date(y[orderParam]);
            return this.sortOrder(a, b, order);
          } else {
            return this.sortOrder(x[orderParam], y[orderParam], order);
          }

        });
      });

      return groups;
  }

  /**
   * Checking given value is date or not
   * @param x
   * @param y
   * @returns {boolean}
   */
  isDate(x: any, y: any): boolean {
    return !isNaN(Date.parse(x)) && !isNaN(Date.parse(y));
  }

  /**
   * sort in ascending or descending format
   * @param x
   * @param y
   * @param {string} order
   * @returns {number}
   */
  sortOrder(x: any, y: any, order: string): number {
    if (order === 'ASC') {
      return ((x == y) ? 0 : ((x > y) ? 1 : -1));
    } else {
      return ((x == y) ? 0 : ((x < y) ? 1 : -1));
    }
  }


  /**
   * Service to group and sort an input array of data based on group and subGroup.
   * If you want to order based on a group then give sub group as blank.
   * The order of the data is done with orderParam attribute.
   * Sorting is based on string sort.
   * @param data
   * @param groupId
   * @param subGroupId
   * @param orderParam
   * @param order
   */
  getGrouppedAndOrderedData(data: any[], groupId: string, subGroupId: string, orderParam: string, order: string) {
    const source = from(data);
    const groups = [];

    const outPut = source.pipe(
      (subGroupId === '' ) ? groupBy(biomarker => biomarker[groupId]) : groupBy(biomarker => biomarker[groupId][subGroupId]),
      mergeMap(group => group.pipe(toArray()))
    );

    outPut.subscribe(val => groups.push(val));
    return this.sortByParam(groups, orderParam, order);
  }

  /**
   * Service to group based on groupIds as an array.
   * Example if you want to group based on a value set then it can given as
   * ["marker", "code"]
   * @param data
   * @param groupIds
   * @param orderParam
   * @param order
   */
  getGrouppedAndOrderedDataByGroupIds(data: any[], groupIds: string[], orderParam: string, order: string) {
    const source = from(data);
    const groups = [];

    const outPut = source.pipe(
      groupBy(biomarker => get(biomarker, groupIds)),
      mergeMap(group => group.pipe(toArray()))
    );

    outPut.subscribe(val => groups.push(val));
    return this.sortByParam(groups, orderParam, order);
  }



  /**
   * Service to group and sort an input array of data based on group array
   * and form a array of structure.
   *
   * @param {any[]} data
   * @param {string[]} groupIds
   * @param {string} orderParam
   * @param {string} order
   * @param groupMaxCount
   * @param showCount
   *
   * @returns {any[]}
   */
  getGrouppedAndOrderedDataStructureByGroupIds(data: any[], groupIds: string[],  orderParam: string, order: string, groupMaxCount ?: number, showCount ?: number): any[] {
    const groupAndOrderedData = this.getGrouppedAndOrderedDataByGroupIds(data, groupIds, orderParam, order);
    const groupsStructure = [];

    groupAndOrderedData.forEach((currentGroups: any[], index: number) => {
      if(groupMaxCount != undefined && showCount != undefined ) {
        for (let i = 0; i < currentGroups.length; i++) {
          if (i < groupMaxCount && groupsStructure.length <= showCount - 1) {
            const groupMainItem: any = currentGroups[i];
            groupsStructure.push(groupMainItem);
          }
        }
      } else {
        const groupMainItem: any =  currentGroups[0];
        groupMainItem.historyData = cloneDeep(currentGroups);
        groupsStructure.push(groupMainItem);
      }
    });

    return groupsStructure;
  }

  getGrouppedAndOrderedDataStructureViewAll(data: any[], groupIds: string[],  orderParam: string, order: string) {
    const groupAndOrderedData  = this.getGrouppedAndOrderedDataByGroupIds(data, groupIds, orderParam, order);
    let groupsStructure = [];
    groupsStructure = this.flatternArray(groupAndOrderedData);
    return groupsStructure;
  }

  getGrouppedAndOrderedDataStructureEditMode(data: any[], groupIds: string[], orderParam: string, order: string) {
    const groupAndOrderedData  = this.getGrouppedAndOrderedDataByGroupIds(data, groupIds, orderParam, order);
    const groupsStructure = [];
    groupAndOrderedData.forEach((currentGroups: any[], index: number) => {
      groupsStructure.push(currentGroups);
    });
    return groupsStructure;
  }

  notifyDataUpdated(data: boolean) {
    this.updatedData = data;
  }

  getNotifyDataUpdated() {
    return this.updatedData;
  }

  sortDataByDate(data: any, orderParam: string, order: string){
    data.sort((a: any, b: any) => {
      const aDate = new Date(get(a, `${orderParam}`))
      const bDate = new Date(get(b, `${orderParam}`))
      if (aDate instanceof Date && bDate instanceof Date) {
        if(!order || order == 'asc') {
          return (aDate.getTime() > bDate.getTime() ? 1 : -1);
        } else {
          return (aDate.getTime() > bDate.getTime() ? -1 : 1);
        }
      }
      if(!order || order == 'asc') {
        return (get(a, `${orderParam}`) > get(b, `${orderParam}`) ? 1 : -1);
      } else {
        return (get(a, `${orderParam}`) > get(b, `${orderParam}`) ? -1 : 1);
      }
    });
  }

}
